import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { LuSmartphone, LuWallet, LuX } from 'react-icons/lu';
import API from '../../api/axios';
import { Button } from '../ui';
import { ROLES, fmtF } from '../../theme';
import './Abonnement.css';

const prixDe = (plan, duree) => {
  if (!plan || !duree) return { brut: 0, total: 0 };
  const brut = Number(plan.prix_mensuel) * duree.mois;
  const total = Math.ceil((brut - Math.round(brut * duree.remise_pct / 100)) / 5) * 5;
  return { brut, total };
};

/**
 * Fenêtre « Payer / renouveler » : plan, durée (avec remise), moyen de paiement
 * (solde prépayé ou paiement en ligne Wave / Orange / MTN / Moov / carte).
 */
/** Frais de paiement en ligne à la charge du client (arrondis au multiple de 5 supérieur). */
export const fraisEnLigne = (montant, pct) => (pct > 0 ? Math.ceil((Number(montant) * pct / 100) / 5) * 5 : 0);

export default function PaiementAbonnement({ abonnement, plans, durees, solde, paiementEnLigne, fraisPourcent = 0, onClose, onPaye, notifier }) {
  const [planId, setPlanId] = useState(abonnement.plan_id || plans[0]?.id);
  const [mois, setMois] = useState(abonnement.duree_renouvellement || durees[0]?.mois || 1);
  const plan = plans.find(p => p.id === Number(planId)) || plans[0];
  const duree = durees.find(d => d.mois === Number(mois)) || durees[0];
  const { brut, total } = useMemo(() => prixDe(plan, duree), [plan, duree]);
  const soldeSuffisant = solde >= total;
  const [moyen, setMoyen] = useState(soldeSuffisant ? 'solde' : 'en_ligne');
  const [envoi, setEnvoi] = useState(false);
  const frais = moyen === 'en_ligne' ? fraisEnLigne(total, fraisPourcent) : 0;
  const aPayer = total + frais;

  useEffect(() => {
    if (moyen === 'solde' && !soldeSuffisant) setMoyen('en_ligne');
  }, [soldeSuffisant, moyen]);

  useEffect(() => {
    const echap = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', echap);
    return () => window.removeEventListener('keydown', echap);
  }, [onClose]);

  const payer = async () => {
    setEnvoi(true);
    try {
      const r = await API.post('/abonnements/payer', {
        etablissement_id: abonnement.etablissement_id, plan_id: plan.id, mois: duree.mois, moyen,
      });
      if (r.data.payment_url) {
        window.location.href = r.data.payment_url; // guichet CinetPay
        return;
      }
      notifier(r.data.message);
      onPaye();
    } catch (e) {
      notifier('❌ ' + (e.response?.data?.message?.replace(/^❌\s*/, '') || 'Erreur réseau'));
      setEnvoi(false);
    }
  };

  const fin = useMemo(() => {
    const base = abonnement.date_fin && new Date(abonnement.date_fin) > new Date() ? new Date(abonnement.date_fin) : new Date();
    const d = new Date(base);
    d.setMonth(d.getMonth() + Number(duree?.mois || 1));
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  }, [abonnement.date_fin, duree]);

  // Rendu dans <body> : un parent animé (transform) décalerait la fenêtre
  return createPortal(
    <div className="ab-modal-fond" style={{ '--accent': ROLES.proprietaire.accent }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ab-modal" role="dialog" aria-modal="true" aria-labelledby="ab-paiement-titre">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <h2 id="ab-paiement-titre">Payer l'abonnement</h2>
            <div className="ab-sub">{abonnement.etablissement_nom}</div>
          </div>
          <button className="ab-bandeau-fermer" aria-label="Fermer" onClick={onClose}><LuX aria-hidden /></button>
        </div>

        <div>
          <div className="ab-champ" style={{ marginBottom: 8 }}>Plan</div>
          <div className="ab-choix">
            {plans.map(p => (
              <button key={p.id} type="button" aria-pressed={p.id === plan?.id} onClick={() => setPlanId(p.id)}>
                <strong>{p.nom}</strong>
                <small className="ab-num">{fmtF(p.prix_mensuel)}/mois</small>
                {p.description && <small>{p.description}</small>}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="ab-champ" style={{ marginBottom: 8 }}>Durée</div>
          <div className="ab-choix">
            {durees.map(d => (
              <button key={d.mois} type="button" aria-pressed={d.mois === duree?.mois} onClick={() => setMois(d.mois)}>
                <strong>{d.mois} mois</strong>
                {d.remise_pct > 0 ? <span className="ab-remise">−{d.remise_pct} %</span> : <small>Sans remise</small>}
              </button>
            ))}
          </div>
        </div>

        <div className="ab-total">
          <div>
            <div className="ab-sub">Total{brut > total ? <> · <s className="ab-num">{fmtF(brut)}</s></> : ''}</div>
            <div className="ab-sub">Valable jusqu'au {fin}</div>
            {frais > 0 && (
              <div className="ab-sub">
                Abonnement <span className="ab-num">{fmtF(total)}</span> + frais de paiement en ligne ({fraisPourcent} %) <span className="ab-num">{fmtF(frais)}</span>
              </div>
            )}
          </div>
          <strong>{fmtF(aPayer)}</strong>
        </div>

        <div className="ab-moyens" role="group" aria-label="Moyen de paiement">
          <button type="button" className="ab-moyen" aria-pressed={moyen === 'solde'}
            disabled={!soldeSuffisant} onClick={() => setMoyen('solde')}>
            <LuWallet aria-hidden />
            <div>
              <strong>Payer avec mon solde</strong>
              <span className="ab-sub">Solde disponible : <span className="ab-num">{fmtF(solde)}</span>{!soldeSuffisant ? ' (insuffisant)' : ' · sans frais'}</span>
            </div>
          </button>
          <button type="button" className="ab-moyen" aria-pressed={moyen === 'en_ligne'}
            disabled={!paiementEnLigne} onClick={() => setMoyen('en_ligne')}>
            <LuSmartphone aria-hidden />
            <div>
              <strong>Payer en ligne</strong>
              <span className="ab-sub">
                {paiementEnLigne
                  ? `Paiement sécurisé par CinetPay${fraisPourcent > 0 ? ` · frais de ${fraisPourcent} %` : ''}`
                  : 'Bientôt disponible'}
              </span>
              <div className="ab-logos"><span>Wave</span><span>Orange Money</span><span>MTN MoMo</span><span>Moov</span><span>Carte bancaire</span></div>
            </div>
          </button>
        </div>

        <div className="ab-actions" style={{ justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={onClose}>Annuler</Button>
          <Button onClick={payer} disabled={envoi || !plan || !duree || (moyen === 'en_ligne' && !paiementEnLigne)}>
            {envoi ? 'Patientez…' : moyen === 'solde' ? `Payer ${fmtF(total)} avec le solde` : `Payer ${fmtF(aPayer)}`}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
